import { useEffect, useRef, useState } from "react";

// ─── PDF pages as pictures, with something drawn on top of each page ─────────
// Used to mark fill-in and signature boxes on an uploaded agreement, and to show the customer where to sign.
// pdf.js is only downloaded when a page actually shows a PDF.

let pdfjsLoading;
function loadPdfjs() {
  pdfjsLoading ||= import("pdfjs-dist/legacy/build/pdf.min.mjs")
    .then(lib => { lib.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString(); return lib; });
  return pdfjsLoading;
}

const RENDER_WIDTH = 1100;   // pixels each page is drawn at; shown scaled to fit

function Page({ pdf, index, overlay }) {
  const canvas = useRef(null);
  const [ratio, setRatio] = useState(1.414);
  useEffect(() => {
    let cancelled = false, task;
    pdf.getPage(index + 1).then(page => {
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });       // includes the page's crop box and rotation
      const viewport = page.getViewport({ scale: RENDER_WIDTH / base.width });
      setRatio(base.height / base.width);
      const c = canvas.current;
      c.width = Math.round(viewport.width); c.height = Math.round(viewport.height);
      task = page.render({ canvas: c, canvasContext: c.getContext("2d"), viewport });
      task.promise.catch(() => {});
    });
    return () => { cancelled = true; task?.cancel(); };
  }, [pdf, index]);
  return (
    <div style={{ position: "relative", width: "100%", aspectRatio: `1 / ${ratio}`, background: "#fff", boxShadow: "0 1px 4px rgba(11,30,61,.18)", marginBottom: 14 }}>
      <canvas ref={canvas} style={{ width: "100%", height: "100%", display: "block" }} />
      {overlay && <div style={{ position: "absolute", inset: 0 }}>{overlay(index)}</div>}
      <div style={{ position: "absolute", right: 6, bottom: 4, fontSize: 11, color: "var(--sub)", pointerEvents: "none" }}>Page {index + 1}</div>
    </div>
  );
}

/** If pdf.js can't run in this browser, show the PDF in the browser's own viewer instead. */
function FallbackViewer({ source, maxHeight }) {
  const [url, setUrl] = useState(typeof source === "string" ? source : null);
  useEffect(() => {
    if (typeof source === "string") { setUrl(source); return; }
    const u = URL.createObjectURL(new Blob([source], { type: "application/pdf" }));
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [source]);
  if (!url) return null;
  return (
    <div>
      <iframe title="Agreement" src={url} style={{ width: "100%", height: maxHeight || "70vh", border: "1px solid #E4EAF2", borderRadius: 8, background: "#fff" }} />
      <a href={url} target="_blank" rel="noopener noreferrer" className="tsm">Open the PDF in a new tab</a>
    </div>
  );
}

/** source: a URL, or the PDF's bytes (ArrayBuffer / Uint8Array). overlay(pageIndex) draws over each page. */
export function PdfPages({ source, overlay, onLoaded, maxHeight }) {
  const [pdf, setPdf] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!source) return;
    let cancelled = false, task;
    setPdf(null); setError("");
    loadPdfjs()
      .then(lib => {
        if (cancelled) return null;
        task = lib.getDocument(typeof source === "string" ? { url: source } : { data: new Uint8Array(source).slice() });
        return task.promise;
      })
      .then(d => { if (d && !cancelled) { setPdf(d); onLoaded?.(d.numPages); } })
      .catch(err => { console.error("PDF viewer:", err); if (!cancelled) setError("This PDF couldn't be shown."); });
    return () => { cancelled = true; task?.destroy?.().catch?.(() => {}); };   // frees the document too
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <FallbackViewer source={source} maxHeight={maxHeight} />;
  if (!pdf) return <div className="tsub tsm">Loading the PDF…</div>;
  return (
    <div style={{ maxHeight, overflow: maxHeight ? "auto" : undefined, background: "#E9EEF4", padding: 12, borderRadius: 8 }}>
      {[...Array(pdf.numPages)].map((_, i) => <Page key={i} pdf={pdf} index={i} overlay={overlay} />)}
    </div>
  );
}
